import {
  describeFailure as describeApiFailure,
  type ApiFailure,
  type FailureCopy,
} from '../../api/client.ts'
import type {
  Booking,
  BookingRequestFidelity,
  BookingRequestReason,
  BookingStatus,
} from '../../api/bookings.ts'
import { assertNever } from '../../assertNever.ts'

// Formatting for the bookings screen.

export const describeFailure = (failure: ApiFailure): FailureCopy =>
  describeApiFailure(failure, 'bookings')

const DATE = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' })
const TIME = new Intl.DateTimeFormat(undefined, { timeStyle: 'short' })

export const formatBookedDate = (iso: string): string => DATE.format(new Date(iso))
export const formatBookedTime = (iso: string): string => TIME.format(new Date(iso))

/** Integer cents → "$1,234.56". Null is "no price recorded", a different fact
 *  from $0.00 — the caller renders the dash. */
export const formatPrice = (cents: number | null): string | null =>
  cents === null
    ? null
    : (cents / 100).toLocaleString(undefined, {
        style: 'currency',
        currency: 'USD',
      })

/**
 * The pill for a status this bundle does not know.
 *
 * A browser holds this JavaScript across a deploy, so a status the API starts
 * returning reaches a bundle compiled before it existed — which is exactly what
 * the `accepted` to `draft` rename does. Indexing the palette for it yields
 * `undefined`, and reading `.tone` off that took the whole row down. Showing the
 * raw code is honest and survives; a stale reader sees the value the API sent.
 */
export const UNKNOWN_STATUS_PILL = (code: string) =>
  ({ label: code, tone: 'neutral' }) as const

/** The design's status pill palette, translated onto the Pill tones. */
export const STATUS_PILL: Record<
  BookingStatus,
  { label: string; tone: 'success' | 'danger' | 'info' | 'neutral' }
> = {
  draft: { label: 'Draft', tone: 'neutral' },
  ready: { label: 'Ready', tone: 'neutral' },
  scheduled: { label: 'Scheduled', tone: 'neutral' },
  assigned_to_driver: { label: 'Assigned', tone: 'info' },
  pickup_in_transit: { label: 'To pickup', tone: 'info' },
  pickup_arrived: { label: 'At pickup', tone: 'info' },
  picked_up: { label: 'Picked up', tone: 'info' },
  dropoff_in_transit: { label: 'En route', tone: 'info' },
  dropoff_arrived: { label: 'At dropoff', tone: 'info' },
  return_in_transit: { label: 'Returning', tone: 'info' },
  return_arrived: { label: 'At return', tone: 'info' },
  holding: { label: 'Holding', tone: 'neutral' },
  delivered: { label: 'Delivered', tone: 'success' },
  returned: { label: 'Returned', tone: 'neutral' },
  failed: { label: 'Canceled', tone: 'danger' },
}

// The wire names references in camelCase (`bolNumber`); a reader recognises
// the business name. Anything outside the three Curri stores today — the map
// is deliberately open — falls back to its wire name rather than being hidden.
const REFERENCE_LABELS: Record<string, string> = {
  bolNumber: 'BOL',
  poNumber: 'PO',
  orderNumber: 'ORDER',
}

/** Display order, most-recognisable first; unknown names follow in map order. */
export const REFERENCE_ORDER = ['bolNumber', 'poNumber', 'orderNumber'] as const

export const referenceLabel = (name: string): string =>
  REFERENCE_LABELS[name] ?? name

/** `BOL-2349` — the row's leading identity, or null when there is none. */
export const primaryReference = (booking: Booking): string | null => {
  for (const name of REFERENCE_ORDER) {
    const value = booking.referenceNumbers[name]
    if (value) return `${referenceLabel(name)}-${value}`
  }
  const [name, value] =
    Object.entries(booking.referenceNumbers).find(([, v]) => Boolean(v)) ?? []
  return name && value ? `${referenceLabel(name)}-${value}` : null
}

/** `BOL 2349 · PO 88-1042` for the identifiers card. */
export const referenceSummary = (booking: Booking): string =>
  Object.entries(booking.referenceNumbers)
    .filter(([, value]) => Boolean(value))
    .map(([name, value]) => `${referenceLabel(name)} ${value}`)
    .join(' · ')

/** One line of a stop for the table cell: the street, or a placeholder when
 *  Curri holds no address for that end. */
export const stopLine = (stop: Booking['pickup']): string =>
  stop.address?.addressLine1 ?? 'No address on record'

export const stopCity = (stop: Booking['pickup']): string =>
  stop.address
    ? `${stop.address.city}, ${stop.address.state}${
        stop.address.postalCode ? ` ${stop.address.postalCode}` : ''
      }`
    : '—'

const compactDims = (item: Booking['items'][number]): string | null => {
  const { lengthIn, widthIn, heightIn } = item
  if (lengthIn === undefined || widthIn === undefined || heightIn === undefined) {
    return null
  }
  return `${lengthIn}×${widthIn}×${heightIn} in`
}

export const itemDims = (item: Booking['items'][number]): string =>
  compactDims(item) ?? '—'

export const itemWeight = (item: Booking['items'][number]): string =>
  item.weightLbs === undefined ? '—' : `${item.weightLbs} lbs`

// ---- the originating request ----

export const describeRequestReason = (reason: BookingRequestReason): string => {
  switch (reason) {
    case 'not_found':
      return 'No request log could be matched to this booking.'
    case 'beyond_retention':
      return 'This booking is older than the request logs reach, so its request is no longer available.'
    case 'unavailable':
      return 'The request logs could not be reached just now. Try again in a moment.'
    default:
      return assertNever(reason, 'request reason')
  }
}

/** The console's mono annotation: what the body is, and how exact. */
export const requestFidelityLabel = (fidelity: BookingRequestFidelity): string => {
  switch (fidelity) {
    case 'json':
      return 'as received'
    case 'logfmt':
      return 'from request logs · values as text'
    case 'raw':
      return 'raw log text'
    default:
      return assertNever(fidelity, 'request fidelity')
  }
}

/** The haystack the client-side search matches against. */
export const searchHaystack = (booking: Booking): string =>
  [
    booking.id,
    booking.deliveryExternalId ?? '',
    Object.values(booking.referenceNumbers).filter(Boolean).join(' '),
    booking.pickup.address?.city ?? '',
    booking.dropoff.address?.city ?? '',
    booking.items.map(item => item.description ?? '').join(' '),
  ]
    .join(' ')
    .toLowerCase()
